import { isNil } from 'lodash-es';

import type {
  ResultRecord,
  ResultRecordValue,
} from '@dynatrace-sdk/client-query';

import type { FieldEntry } from '../getFieldsArrayForRecordIndex.js';
import { GENERIC_KEY_FIELD_NAMES } from '../utils/generic-key-field-names.js';

/**
 * @public
 */
export interface Dimension {
  field: FieldEntry;
  value: ResultRecordValue | undefined;
}

/**
 * Generates a key for a record based on its fields
 * @public
 */
export function getDimensions(
  record: ResultRecord,
  fieldsArray: FieldEntry[],
  isTimeseriesFormat: boolean,
): Array<Dimension> {
  return fieldsArray
    .map((field) => {
      if (isTimeseriesFormat || !GENERIC_KEY_FIELD_NAMES.includes(field.name)) {
        const value = record[field.name];
        if (
          !isNil(value) &&
          (field.type === 'string' ||
            field.type === 'boolean' ||
            field.type === 'geo_point' ||
            field.type === 'ip_address' ||
            field.type === 'uid')
        ) {
          return { field, value };
        } else if (isTimeseriesFormat === false && field.type === 'array') {
          return { field, value };
        }
      }
      return undefined;
    })
    .filter((x): x is Dimension => !!x);
}

/**
 * Gets the value dimensions from field entries.
 * @param fieldsArray - Field entries
 * @param isTimeseriesFormat - Boolean indicating if field entry is in timeseries format
 
* @internal
*/
export function getValueDimensions(
  fieldsArray: FieldEntry[],
  isTimeseriesFormat: boolean,
) {
  let valueDimensions: FieldEntry[];

  if (isTimeseriesFormat) {
    valueDimensions = fieldsArray.filter(({ type }) => type === 'array');
  } else {
    valueDimensions = fieldsArray.filter(({ type }) =>
      ['double', 'long', 'duration'].includes(type),
    );
  }

  return valueDimensions;
}
